import React, { useCallback, useRef, useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, Platform, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useDeckStore, useAuthStore } from '@/store';
import { MIN_DECK_COLUMN_WIDTH, MAX_DECK_COLUMN_WIDTH } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import DeckColumn from './DeckColumn';
import DeckRefColumn from './DeckRefColumn';
import DeckColumnResizeHandle from './DeckColumnResizeHandle';
import DeckEmptyState from './DeckEmptyState';
import DeckTabBar from './DeckTabBar';
import DeckMockup from './DeckMockup';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const EMPTY_COLUMNS = [];

const REMOVE_ANIMATION_MS = 200;
const ADD_ANIMATION_MS = 50;
const SCROLL_AFTER_ADD_MS = 100;
const NARROW_VIEWPORT_PADDING = 60;
const DRAG_SLOP_PX = 8;

export default function DeckView() {
  const { colors } = useTheme();
  const { windowWidth } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const userId = useAuthStore(state => state.user?.id);

  // ── Multi-deck plumbing ────────────────────────────────────────────────
  const decks = useDeckStore(state => state.decks);
  const activeSlug = useDeckStore(state => state.activeDeckSlug);
  const isLoadingDecks = useDeckStore(state => state.isLoadingDecks);
  const hasLoadedOnce = useDeckStore(state => state.hasLoadedOnce);
  const syncError = useDeckStore(state => state.syncError);

  // Active-deck mirrors (legacy column shape). These rerender when the
  // active deck's columns change.
  const columns = useDeckStore(state => state.columns);
  const focusedColumnId = useDeckStore(state => state.focusedColumnId);
  // `state.actions` is a stable reference in zustand stores, so destructuring
  // here costs us nothing per render and keeps the body tidy.
  const {
    loadDecks,
    retrySync,
    setActiveDeckSlug,
    addColumn,
    removeColumn: storeRemoveColumn,
    setColumnWidth,
    equalizeColumnWidths,
    reorderColumns,
    setFocusedColumnId,
  } = useDeckStore(state => state.actions);

  const scrollRef = useRef(null);

  // Pull decks from the server once per session. We rely on persist() for
  // the offline cache, so the page renders instantly while this resolves.
  useEffect(() => {
    if (!userId) return;
    loadDecks();
  }, [userId, loadDecks]);

  // Sync `?d=` with the active deck without fighting tab clicks.
  //
  // Bug we fixed: when the user switches tabs, `activeSlug` updates
  // immediately but `urlSlug` can still be the *previous* deck for a frame
  // (or longer). Syncing "url !== active → setActive(url)" in that window
  // reverted the selection and could recurse with `router.setParams`
  // ("Maximum update depth exceeded").
  //
  // Rule: only apply URL → store when the *URL* slug actually changed
  // (browser back/forward, deep link). One-time store → URL when /deck
  // loads with no `?d=`.
  const urlSlug = typeof params?.d === 'string'
    ? params.d
    : (Array.isArray(params?.d) ? params.d[0] : null);
  /** `undefined` = effect has never run — needed so cold loads still honor `?d=` vs persisted active deck. */
  const prevUrlSlugRef = useRef(undefined);
  const didInitUrlParamRef = useRef(false);

  useEffect(() => {
    if (!decks.length) return;

    const prevUrl = prevUrlSlugRef.current;
    const urlChanged = prevUrl !== urlSlug;
    if (urlSlug && decks.some(d => d.slug === urlSlug)) {
      prevUrlSlugRef.current = urlSlug;
      const shouldSyncFromUrl =
        urlSlug !== activeSlug && (urlChanged || prevUrl === undefined);
      if (shouldSyncFromUrl) {
        setActiveDeckSlug(urlSlug);
      }
      didInitUrlParamRef.current = true;
      return;
    }

    // Wait for hydration before rejecting a deep link absent from the cache.
    if (urlSlug && (!hasLoadedOnce || isLoadingDecks)) return;
    if (Platform.OS === 'web' && activeSlug && (urlSlug || !didInitUrlParamRef.current)) {
      prevUrlSlugRef.current = urlSlug;
      didInitUrlParamRef.current = true;
      try {
        router.setParams({ d: activeSlug });
      } catch (_) { /* no-op */ }
    }
  }, [decks, urlSlug, activeSlug, hasLoadedOnce, isLoadingDecks, setActiveDeckSlug, router]);

  // ── Narrow viewport warning (web/macOS only) ───────────────────────────
  const isTooNarrow = windowWidth < MIN_DECK_COLUMN_WIDTH + NARROW_VIEWPORT_PADDING;
  const [dismissedNarrowWarning, setDismissedNarrowWarning] = useState(false);
  const showNarrowWarning = isTooNarrow && !dismissedNarrowWarning;
  const dismissNarrowWarning = useCallback(() => setDismissedNarrowWarning(true), []);

  // ── Column add/remove transitions ──────────────────────────────────────
  const [removingIds, setRemovingIds] = useState(() => new Set());
  const [animatingInIds, setAnimatingInIds] = useState(() => new Set());

  // Track all in-flight transition timers so we can cancel them if we
  // unmount mid-animation (avoids "set state on unmounted component" warns
  // in dev and stale store mutations after navigation).
  const timersRef = useRef(new Set());
  const startTimer = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, ms);
    timersRef.current.add(id);
    return id;
  }, []);
  useEffect(() => () => {
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current.clear();
  }, []);

  const removeColumn = useCallback((columnId) => {
    if (Platform.OS === 'web') {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.add(columnId);
        return next;
      });
      startTimer(() => {
        storeRemoveColumn(columnId, activeSlug);
        setRemovingIds(prev => {
          if (!prev.has(columnId)) return prev;
          const next = new Set(prev);
          next.delete(columnId);
          return next;
        });
      }, REMOVE_ANIMATION_MS);
    } else {
      storeRemoveColumn(columnId, activeSlug);
    }
  }, [storeRemoveColumn, startTimer, activeSlug]);

  // ── Drag-and-drop column reorder ───────────────────────────────────────
  const [dragState, setDragState] = useState({ draggingId: null, dropIndex: -1 });
  const columnRefs = useRef({});
  const dragCleanupRef = useRef(null);

  const orderedColumns = columns ?? EMPTY_COLUMNS;

  const handleAddColumn = useCallback((spec = {}) => {
    const newColId = addColumn(spec);
    if (!newColId) return;
    setAnimatingInIds(prev => {
      const next = new Set(prev);
      next.add(newColId);
      return next;
    });
    startTimer(() => {
      setAnimatingInIds(prev => {
        if (!prev.has(newColId)) return prev;
        const next = new Set(prev);
        next.delete(newColId);
        return next;
      });
    }, ADD_ANIMATION_MS);
    if (scrollRef.current) {
      startTimer(() => {
        scrollRef.current?.scrollToEnd?.({ animated: true });
      }, SCROLL_AFTER_ADD_MS);
    }
  }, [addColumn, startTimer]);

  const handleResize = useCallback((leftColumnId, delta) => {
    const fresh = useDeckStore.getState().columns.find(c => c.id === leftColumnId);
    if (!fresh) return;
    const newWidth = Math.max(
      MIN_DECK_COLUMN_WIDTH,
      Math.min(MAX_DECK_COLUMN_WIDTH, fresh.width + delta),
    );
    setColumnWidth(leftColumnId, newWidth);
  }, [setColumnWidth]);

  const handleDoubleClickResize = useCallback(() => {
    equalizeColumnWidths();
  }, [equalizeColumnWidths]);

  const latestDropIndex = useRef(-1);

  const handleColumnDragStart = useCallback((columnId, e) => {
    if (Platform.OS !== 'web') return;

    const startX = e.clientX;
    const startY = e.clientY;
    let hasMoved = false;
    latestDropIndex.current = -1;

    const currentCols = useDeckStore.getState().columns;
    const fromIndex = currentCols.findIndex(c => c.id === columnId);
    if (fromIndex === -1) return;

    const computeDropIndex = (mouseX) => {
      const refs = columnRefs.current;
      const cols = useDeckStore.getState().columns;
      for (let i = 0; i < cols.length; i++) {
        const el = refs[cols[i].id];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        if (mouseX < midX) return i;
      }
      return cols.length;
    };

    const onMove = (moveEvent) => {
      if (!hasMoved) {
        const dx = Math.abs(moveEvent.clientX - startX);
        const dy = Math.abs(moveEvent.clientY - startY);
        if (dx < DRAG_SLOP_PX && dy < DRAG_SLOP_PX) return;
        hasMoved = true;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }

      const dropIdx = computeDropIndex(moveEvent.clientX);
      latestDropIndex.current = dropIdx;
      setDragState({ draggingId: columnId, dropIndex: dropIdx });
    };

    const cleanup = ({ commit } = { commit: false }) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragCleanupRef.current = null;

      if (commit && hasMoved) {
        const cols = useDeckStore.getState().columns;
        const currentFrom = cols.findIndex(c => c.id === columnId);
        const dropIdx = latestDropIndex.current;
        if (currentFrom !== -1 && dropIdx !== -1) {
          let toIndex = dropIdx;
          if (toIndex > currentFrom) toIndex -= 1;
          if (toIndex !== currentFrom) reorderColumns(currentFrom, toIndex);
        }
      }
      setDragState({ draggingId: null, dropIndex: -1 });
    };

    const onUp = () => cleanup({ commit: true });

    dragCleanupRef.current = () => cleanup({ commit: false });
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [reorderColumns]);

  // Tear down a drag-in-progress if we unmount mid-drag (otherwise the
  // document-level listeners survive the component and stomp on the next
  // page that mounts).
  useEffect(() => () => {
    dragCleanupRef.current?.();
  }, []);

  const setColumnRef = useCallback((columnId, ref) => {
    if (ref) columnRefs.current[columnId] = ref;
    else delete columnRefs.current[columnId];
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') return undefined;
    if (typeof document === 'undefined') return undefined;

    const handleKeyDown = (e) => {
      const isCtrlOrMeta = e.ctrlKey || e.metaKey;
      if (!isCtrlOrMeta) return;

      if (e.shiftKey && e.key === 'N') {
        e.preventDefault();
        handleAddColumn();
        return;
      }
      if (e.shiftKey && e.key === 'W') {
        e.preventDefault();
        const focused = useDeckStore.getState().focusedColumnId;
        if (focused) removeColumn(focused);
        return;
      }
      if (e.shiftKey && (e.code === 'BracketLeft' || e.key === '[' || e.key === '{')) {
        e.preventDefault();
        const focused = useDeckStore.getState().focusedColumnId;
        if (focused) useDeckStore.getState().actions.moveColumnLeft(focused);
        return;
      }
      if (e.shiftKey && (e.code === 'BracketRight' || e.key === ']' || e.key === '}')) {
        e.preventDefault();
        const focused = useDeckStore.getState().focusedColumnId;
        if (focused) useDeckStore.getState().actions.moveColumnRight(focused);
        return;
      }
      if (!e.shiftKey && e.key >= '1' && e.key <= '9') {
        const idx = parseInt(e.key, 10) - 1;
        const cols = useDeckStore.getState().columns;
        if (idx < cols.length) {
          e.preventDefault();
          setFocusedColumnId(cols[idx].id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleAddColumn, removeColumn, setFocusedColumnId]);

  // ── Empty state: zero decks → big mockup hero ──────────────────────────
  // Use cached state during the first load so the page doesn't flash to
  // the empty state and back. Once we've fetched at least once, trust the
  // server's authoritative answer.
  if (decks.length === 0) {
    if (!hasLoadedOnce && isLoadingDecks) {
      return (
        <View style={[styles.loading, { backgroundColor: colors.solid_color_primary }]}>
          <Icon name="spinner" size={20} color={colors.text_color_tertiary} />
        </View>
      );
    }
    return (
      <View style={styles.container}>
        {syncError && <DeckSyncError message={syncError} onRetry={retrySync} colors={colors} />}
        <DeckMockup />
      </View>
    );
  }

  const isDragging = dragState.draggingId !== null;
  const dragFromIndex = isDragging ? orderedColumns.findIndex(c => c.id === dragState.draggingId) : -1;
  const lastIndex = orderedColumns.length - 1;

  return (
    <View style={[styles.container, { backgroundColor: colors.solid_color_secondary }]}>
      <DeckTabBar onAddColumn={handleAddColumn} columnCount={orderedColumns.length} />
      {syncError && <DeckSyncError message={syncError} onRetry={retrySync} colors={colors} />}
      {showNarrowWarning && (
        <View style={[styles.narrowBanner, { backgroundColor: colors.solid_color_block, borderBottomColor: colors.border_color_secondary }]}>
          <Icon name="triangle-exclamation" size={14} color={colors.text_color_secondary} />
          <Text size="xs" color="secondary" style={styles.narrowBannerText}>
            Your screen may be too narrow for the deck view. Try widening your browser or hiding the sidebar.
          </Text>
          <Pressable
            onPress={dismissNarrowWarning}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Dismiss warning"
          >
            <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
          </Pressable>
        </View>
      )}
      {orderedColumns.length === 0 ? <DeckEmptyState onAddColumn={() => handleAddColumn()} /> : <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {orderedColumns.map((col, index) => {
          const showDropBefore = isDragging
            && dragState.dropIndex === index
            && dragFromIndex !== index
            && dragFromIndex !== index - 1;
          const showDropAfter = isDragging
            && dragState.dropIndex === orderedColumns.length
            && index === lastIndex
            && dragFromIndex !== index;

          // Conversation columns use the chat-aware DeckColumn. Other
          // ref kinds (model/agent/tool) render the placeholder until
          // their runtime support ships.
          const ColumnRenderer = (col.type && col.type !== 'conversation') ? DeckRefColumn : DeckColumn;

          return (
            <React.Fragment key={col.id}>
              {showDropBefore && (
                <View style={[styles.dropIndicator, { backgroundColor: colors.text_color_primary }]} />
              )}
              <ColumnRenderer
                column={col}
                deckSlug={activeSlug}
                columnIndex={index}
                totalColumns={orderedColumns.length}
                isFocused={focusedColumnId === col.id}
                onDragStart={handleColumnDragStart}
                setRef={setColumnRef}
                isBeingDragged={dragState.draggingId === col.id}
                isAnimatingIn={animatingInIds.has(col.id)}
                isRemoving={removingIds.has(col.id)}
              />
              {index < lastIndex && !isDragging && (
                <DeckColumnResizeHandle
                  onResize={(delta) => handleResize(col.id, delta)}
                  onDoubleClick={handleDoubleClickResize}
                />
              )}
              {index < lastIndex && isDragging && (
                <View style={styles.dragSpacer} />
              )}
              {showDropAfter && (
                <View style={[styles.dropIndicator, { backgroundColor: colors.text_color_primary }]} />
              )}
            </React.Fragment>
          );
        })}
      </ScrollView>}
    </View>
  );
}

function DeckSyncError({ message, onRetry, colors }) {
  return (
    <View accessibilityRole="alert" style={[styles.narrowBanner, { backgroundColor: colors.solid_color_block }]}>
      <Text size="xs" color="secondary" style={styles.narrowBannerText}>{message}</Text>
      <Pressable accessibilityRole="button" accessibilityLabel="Retry deck sync" onPress={onRetry}>
        <Text size="xs" color="primary">Retry</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: 16,
    ...(Platform.OS === 'macos' || Platform.OS === 'web'
      ? { height: '100%', minHeight: '100%' }
      : { minHeight: '100%' }),
    ...(Platform.OS === 'web' && {
      scrollBehavior: 'smooth',
    }),
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  narrowBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  narrowBannerText: {
    flex: 1,
  },
  dropIndicator: {
    width: 3,
    flexShrink: 0,
    borderRadius: 2,
  },
  dragSpacer: {
    width: 12,
    flexShrink: 0,
  },
});
